import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { adminApi } from '../../services/api';
import CommunicationComposer from '../shared/CommunicationComposer';

const AddActivityModal = ({ show, onHide, onSave, eventId, event, shouldPulse = false }) => {
  const cardRef = useRef(null);
  const currentUser = useSelector((state) => state.user.currentUser);
  const [formData, setFormData] = useState({
    event_outcome: '',
    channel: '',
    direction: '',
    call_domain: '',
    customer_type: '',
    notes: '',
    follow_up_required: false,
    follow_up_date: '',
    follow_up_notes: '',
    assigned_to: '',
    activity_metadata: {},
    novu_data: {},
    teams_data: {},
    phone_data: {},
    communication_metadata: {},
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showCommunicationComposer, setShowCommunicationComposer] = useState(true);
  const [isAnkrAdmin, setIsAnkrAdmin] = useState(false);
  const [queuedCommunications, setQueuedCommunications] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [modalOpenedOnce, setModalOpenedOnce] = useState(false);

  // Scroll and pulse effect when modal opens with shouldPulse
  useEffect(() => {
    if (show && shouldPulse && cardRef.current) {
      // Scroll to the card smoothly
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Add pulse animation classes
        cardRef.current?.classList.add('border-danger', 'pulse-border-danger');

        // Remove pulse after animation completes
        setTimeout(() => {
          cardRef.current?.classList.remove('pulse-border-danger');
        }, 2000);
      }, 100);
    }
  }, [show, shouldPulse]);

  // Fetch users when modal opens and initialize form with event data
  useEffect(() => {
    if (show) {
      const initialData = {
        event_outcome: event?.outcome || '',
        channel: event?.channel || '',
        direction: event?.direction || '',
        call_domain: event?.call_domain || '',
        customer_type: event?.customer_type || '',
        notes: '',
        follow_up_required: false,
        follow_up_date: '',
        follow_up_notes: '',
        assigned_to: event?.event_metadata?.assigned_to || '',
        activity_metadata: {
          priority: event?.event_metadata?.priority || ''
        },
        novu_data: {},
        teams_data: {},
        phone_data: {},
        communication_metadata: {},
      };
      setFormData(initialData);
      setInitialFormData(initialData);
      setErrors({});
      setShowCommunicationComposer(true);

      // Only clear queue on first open, not on re-renders
      if (!modalOpenedOnce) {
        setQueuedCommunications([]);
        setModalOpenedOnce(true);
      }

      setUserSearchQuery('');

      // Determine if Ankr admin from Redux
      setIsAnkrAdmin(currentUser?.isAnkrAdmin || false);

      // Fetch users list
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const response = await adminApi.getUsers({ page_size: 1000, response_format: 'minimal' });
          const usersData = response.data?.users || response.data?.results || response.data || [];

          // Filter to only staff users (ankr and healthcare), exclude patients
          let staffUsers = Array.isArray(usersData)
            ? usersData.filter(user => user.user_type === 'ankr' || user.user_type === 'healthcare')
            : [];

          // Sort alphabetically by lastname, firstname within each user type
          staffUsers = staffUsers.sort((a, b) => {
            // First sort by user type (ankr first, then healthcare)
            if (a.user_type !== b.user_type) {
              return a.user_type === 'ankr' ? -1 : 1;
            }

            // Within same user type, sort by lastname, then firstname
            const lastNameA = (a.last_name || '').toLowerCase();
            const lastNameB = (b.last_name || '').toLowerCase();
            const firstNameA = (a.first_name || '').toLowerCase();
            const firstNameB = (b.first_name || '').toLowerCase();

            if (lastNameA !== lastNameB) {
              return lastNameA.localeCompare(lastNameB);
            }
            return firstNameA.localeCompare(firstNameB);
          });

          setUsers(staffUsers);
        } catch (error) {
          setUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      };

      fetchUsers();
    } else {
      // Reset when modal closes so queue clears on next open
      setModalOpenedOnce(false);
    }
  }, [show, event, currentUser?.isAnkrAdmin, modalOpenedOnce]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If follow_up_required is being checked, set follow_up_date to today
    if (name === 'follow_up_required' && checked) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const dateTimeLocalValue = `${year}-${month}-${day}T${hours}:${minutes}`;

      setFormData(prev => ({
        ...prev,
        [name]: checked,
        follow_up_date: dateTimeLocalValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCommunicationQueued = (channel, communicationRecord) => {
    // Check if this channel already has a queued communication
    setQueuedCommunications(prev => {
      const existingIndex = prev.findIndex(item => item.channel === channel);
      if (existingIndex !== -1) {
        // Replace existing entry
        const updated = [...prev];
        updated[existingIndex] = { channel, record: communicationRecord };
        return updated;
      }
      // Add new entry
      return [...prev, { channel, record: communicationRecord }];
    });

    // Update the activity description to show queued communication
    const communicationSummary = `${channel.toUpperCase()} queued for ${communicationRecord.to}`;
    const communicationNote = `⏱ ${communicationSummary}`;

    setFormData(prev => {
      let updatedNotes = prev.notes || '';

      // Check if there's already a queued entry for this channel in notes
      const existingPattern = new RegExp(`⏱ ${channel.toUpperCase()} queued[^\n]*`, 'gi');
      if (existingPattern.test(updatedNotes)) {
        // Replace existing line
        updatedNotes = updatedNotes.replace(existingPattern, communicationNote);
      } else {
        // Add new line
        updatedNotes = updatedNotes ? `${updatedNotes}\n${communicationNote}` : communicationNote;
      }

      return { ...prev, notes: updatedNotes };
    });
  };

  const handleAssignMe = () => {
    const djangoUserId = currentUser?.djangoUserId;
    const userName = currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.lastName}, ${currentUser.firstName}`
      : currentUser?.email || currentUser?.username || 'Unknown';


    if (!djangoUserId) {
      alert('Unable to assign: You are using an admin/general token without a user profile. Please select from list instead.');
      return;
    }

    // Use Django user ID directly (no need to search in users list)
    setFormData(prev => ({ ...prev, assigned_to: djangoUserId }));
    const displayName = currentUser?.email ? `${userName} (${currentUser.email})` : userName;
    setUserSearchQuery(displayName);
  };

  // Handle user selection from datalist input
  const handleUserInputChange = (e) => {
    const value = e.target.value;
    setUserSearchQuery(value);

    // Try to match entered text to a user
    if (value) {
      const matchedUser = users.find(u => {
        const baseName = u.first_name && u.last_name
          ? `${u.last_name}, ${u.first_name}`
          : u.email || u.username;
        const displayName = u.email ? `${baseName} (${u.email})` : baseName;
        return displayName.toLowerCase() === value.toLowerCase();
      });

      if (matchedUser && matchedUser.id) {
        // Store Django user ID (integer), not profile UUID
        setFormData(prev => ({ ...prev, assigned_to: matchedUser.id }));
      }
    } else {
      // Clear assignment if input is empty
      setFormData(prev => ({ ...prev, assigned_to: '' }));
    }
  };

  const hasFormChanged = () => {
    if (!initialFormData) return false;
    // Check if any field has changed from initial values
    const fieldsToCheck = ['event_outcome', 'channel', 'direction', 'call_domain', 'customer_type', 'notes', 'follow_up_required', 'follow_up_date', 'follow_up_notes', 'assigned_to'];
    for (const field of fieldsToCheck) {
      if (formData[field] !== initialFormData[field]) {
        return true;
      }
    }
    // Check if priority changed
    if (formData.activity_metadata?.priority !== initialFormData.activity_metadata?.priority) {
      return true;
    }
    // Check if any communications queued
    if (queuedCommunications.length > 0) {
      return true;
    }
    return false;
  };

  const validate = () => {
    const newErrors = {};
    // No validation errors - all fields are optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      // Track values outside of state for use in cleanedData
      let updatedNotes = formData.notes;
      let sentCommunications = [];

      // Send all queued communications first
      if (queuedCommunications.length > 0) {
        const sendPromises = queuedCommunications.map(async ({ channel, record }) => {
          try {
            let response;
            switch (channel) {
              case 'sms':
                {
                  // Extract customer name from various sources
                  const customerName = event?.customer_name
                    || event?.patient?.name
                    || event?.patient_name
                    || (event?.extracted_patient_data?.patient_first_name && event?.extracted_patient_data?.patient_last_name
                        ? `${event.extracted_patient_data.patient_first_name} ${event.extracted_patient_data.patient_last_name}`
                        : null)
                    || event?.extracted_patient_data?.patient_first_name  // Just first name if last name is missing
                    || null;

                  response = await adminApi.sendSMS({
                    recipient_type: record.recipient_type || 'vcx_event',
                    recipient_id: record.recipient_id || event?.id,
                    workflow_id: record.workflow_id,
                    phone: record.to,
                    message: record.message,
                    customer_name: customerName
                  });
                }
                break;

              case 'email':
                {
                  // Extract customer name from various sources
                  const customerName = event?.customer_name
                    || event?.patient?.name
                    || event?.patient_name
                    || (event?.extracted_patient_data?.patient_first_name && event?.extracted_patient_data?.patient_last_name
                        ? `${event.extracted_patient_data.patient_first_name} ${event.extracted_patient_data.patient_last_name}`
                        : null)
                    || event?.extracted_patient_data?.patient_first_name  // Just first name if last name is missing
                    || null;

                  response = await adminApi.sendEmail({
                    recipient_type: record.recipient_type || 'vcx_event',
                    recipient_id: record.recipient_id || event?.id,
                    workflow_id: record.workflow_id,
                    email: record.to,
                    subject: record.subject,
                    body: record.body,
                    customer_name: customerName,
                    track_opens: true  // Enable email tracking
                  });
                }
                break;

              case 'teams':
                response = await adminApi.sendTeamsMessage({
                  message: record.message,
                  message_type: record.message_type || 'channel',
                  channel_id: record.message_type === 'channel' ? record.to : undefined,
                  user_id: record.message_type === 'user' ? record.to : undefined
                });
                break;

              case 'call':
                response = await adminApi.initiateCall({
                  to_phone: record.to,
                  purpose: record.purpose
                });
                break;

              default:
                throw new Error(`Unknown communication channel: ${channel}`);
            }

            // Update the record with sent status and tracking data from response
            const sentRecord = {
              ...record,
              status: 'sent',
              sent_at: new Date().toISOString(),
              // Include tracking info from response if available
              ...(response.data?.tracking_id && {
                tracking_id: response.data.tracking_id,
                email_opened: false,
                opened_at: null
              }),
              ...(response.data?.activity_id && { activity_id: response.data.activity_id })
            };

            return { channel, success: true, response, sentRecord };
          } catch (error) {
            return { channel, success: false, error: error.message };
          }
        });

        // Wait for all communications to send
        const results = await Promise.all(sendPromises);

        // Collect sent communications for novu_data
        sentCommunications = results
          .filter(r => r.success && r.sentRecord)
          .map(r => r.sentRecord);

        // Update notes field to replace "queued" with "sent"
        const successCount = results.filter(r => r.success).length;
        const failCount = results.length - successCount;

        // Replace all "⏱ ... queued" with "✓ ... sent" for successful sends
        results.forEach(({ channel, success }) => {
          if (success) {
            const queuedPattern = new RegExp(`⏱ (${channel.toUpperCase()} queued[^\n]*)`, 'g');
            updatedNotes = updatedNotes.replace(queuedPattern, (_, captureGroup) => {
              return `✓ ${captureGroup.replace('queued for', 'sent to')}`;
            });
          }
        });

        // Update state for UI display
        setFormData(prev => ({ ...prev, notes: updatedNotes }));

        // Show summary alert
        if (failCount > 0) {
          alert(`Sent ${successCount} communication(s). ${failCount} failed. Check console for details.`);
        }
      }

      // Clean up the data before sending
      const cleanedData = {
        ...formData,
        notes: updatedNotes, // Use the updated notes with replaced "queued" → "sent"
        // Add required activity_type field (default to 'updated_status' since we removed the dropdown)
        activity_type: 'updated_status',
        // Convert empty strings to null for date fields
        follow_up_date: formData.follow_up_date || null,
        // Build novu_data from sent communications (preserves tracking_id, email_opened, etc.)
        novu_data: sentCommunications.length > 0 ? { communications: sentCommunications } : {}
      };

      // Only include event fields if they've changed from the original event values
      if (formData.event_outcome !== (event?.outcome || '')) {
        cleanedData.event_outcome = formData.event_outcome || null;
      }
      if (formData.channel !== (event?.channel || '')) {
        cleanedData.channel = formData.channel || null;
      }
      if (formData.direction !== (event?.direction || '')) {
        cleanedData.direction = formData.direction || null;
      }
      if (formData.call_domain !== (event?.call_domain || '')) {
        cleanedData.event_call_domain = formData.call_domain || null;
      }
      if (formData.customer_type !== (event?.customer_type || '')) {
        cleanedData.customer_type = formData.customer_type || null;
      }

      // Convert empty string to null, or keep the UUID if present
      const currentAssignedTo = event?.event_metadata?.assigned_to || '';
      if (formData.assigned_to !== currentAssignedTo) {
        if (formData.assigned_to && String(formData.assigned_to).trim() !== '') {
          cleanedData.assigned_to = String(formData.assigned_to).trim();
        } else {
          cleanedData.assigned_to = null;
        }
      }

      // Clean up activity_metadata - remove if empty or unchanged
      const currentPriority = event?.event_metadata?.priority || '';
      if (formData.activity_metadata?.priority && formData.activity_metadata.priority !== currentPriority) {
        cleanedData.activity_metadata = { priority: formData.activity_metadata.priority };
      } else if (!formData.activity_metadata?.priority) {
        cleanedData.activity_metadata = {};
      }

      await onSave(cleanedData);
      onHide();
    } catch (error) {
      setErrors({ general: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to save activity' });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes pulse-border {
          0%, 100% {
            border-color: #dc3545;
            border-width: 2px;
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
          }
          50% {
            border-color: #dc3545;
            border-width: 3px;
            box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
          }
        }
        .pulse-border-danger {
          animation: pulse-border 2s ease-in-out;
        }
      `}</style>
      <div ref={cardRef} className="card mb-3 p-3" style={{ transition: 'border-color 0.3s ease' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title text-primary border-0 mb-0">Take Action</h5>
            <p className='text-muted text-sm'>Track what you did for this event, send SMS or Teams chats, and more</p>
          </div>
          <button type="button" className="btn-close" onClick={onHide}></button>
        </div>

        <form onSubmit={handleSubmit}>
        <div className="">
          {errors.general && (
            <div className="alert alert-danger">{errors.general}</div>
          )}

              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  rows="3"
                  className={`form-control ${errors.notes ? 'is-invalid' : ''}`}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Describe the action taken..."
                />
                {errors.notes && <div className="invalid-feedback">{errors.notes}</div>}
              </div>

              {/* Event Classification Section */}
              <hr className="my-3" />
              <h6 className="text-muted mb-3">Modify Basic Parameters (Optional)</h6>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">Domain</label>
                  <select
                    className="form-select form-select-sm"
                    name="call_domain"
                    value={formData.call_domain}
                    onChange={handleChange}
                  >
                    <option value="">None</option>
                    <option value="appointments">📅 Appointments</option>
                    <option value="medications">💊 Medications</option>
                    <option value="medicalIssues">🏥 Medical Issues</option>
                    <option value="billingInsurance">💳 Billing/Insurance</option>
                    <option value="medicalRecords">📋 Medical Records</option>
                    <option value="new_patient">🆕 New Patient</option>
                    <option value="generalInquiries">ℹ️ General Inquiries</option>
                    <option value="providerCalls">👨‍⚕️ Provider Calls</option>
                    <option value="vendorCalls">📞 Vendor Calls</option>
                    <option value="unknown">❓ Unknown/Other</option>
                  </select>
                </div>

                <div className="col-md-6 mb-2">
                  <label className="form-label">Outcome</label>
                  <select
                    className="form-select form-select-sm"
                    name="event_outcome"
                    value={formData.event_outcome}
                    onChange={handleChange}
                  >
                    <option value="">None</option>
                    <option value="new">⚠ New</option>
                    <option value="in_progress">⏱ In Progress</option>
                    <option value="resolved">✓ Resolved</option>
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Caller Type</label>
                  <select
                    className="form-select form-select-sm"
                    name="customer_type"
                    value={formData.customer_type}
                    onChange={handleChange}
                  >
                    <option value="">None</option>
                    <option value="patient">Patient</option>
                    <option value="family">Family</option>
                    <option value="vendor">Vendor</option>
                    <option value="insurance">Insurance</option>
                    <option value="other_provider">Other Provider</option>
                  </select>
                </div>
              </div>

              {/* Triage & Assignment Section */}
              <hr className="my-3" />
              <h6 className="text-muted mb-3">Triage & Assignment (Optional)</h6>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">Priority Level</label>
                  <select
                    className="form-select form-select-sm"
                    name="priority"
                    value={formData.activity_metadata?.priority || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        activity_metadata: {
                          ...prev.activity_metadata,
                          priority: e.target.value
                        }
                      }));
                    }}
                  >
                    <option value="">— Not Set —</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

              <div className="col-md-6 mb-2">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label mb-0">Assign To</label>
                  {(() => {
                    const djangoUserId = currentUser?.djangoUserId;

                    // Hide button for virtual users (admin/general tokens with no Django user ID)
                    if (!djangoUserId) {
                      return null;
                    }

                    // Hide button if already assigned to me in the saved event
                    const savedAssignedTo = event?.event_metadata?.assigned_to;
                    const isAssignedToMe = String(savedAssignedTo) === String(djangoUserId);
                    if (isAssignedToMe) {
                      return null;
                    }

                    return (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAssignMe}>
                        Assign Me
                      </button>
                    );
                  })()}
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Type to search users or double click to view..."
                  value={userSearchQuery}
                  onChange={handleUserInputChange}
                  disabled={loadingUsers}
                  list="users-list"
                  autoComplete="off"
                />
                <datalist id="users-list">
                  {users.map(user => {
                    const baseName = user.first_name && user.last_name
                      ? `${user.last_name}, ${user.first_name}`
                      : user.email || user.username;
                    // Append email to ensure uniqueness if multiple users share same name
                    const displayName = user.email ? `${baseName} (${user.email})` : baseName;
                    return <option key={user.id} value={displayName} />;
                  })}
                </datalist>
                {loadingUsers && <small className="text-muted d-block mt-1">Loading users...</small>}
                {formData.assigned_to && (() => {
                  // Match by Django user ID (integer)
                  const assignedUser = users.find(u => u.id === formData.assigned_to);
                  const displayName = (() => {
                    if (!assignedUser) return userSearchQuery || 'selected user';
                    const baseName = assignedUser.first_name && assignedUser.last_name
                      ? `${assignedUser.last_name}, ${assignedUser.first_name}`
                      : assignedUser.email || assignedUser.username;
                    return assignedUser.email ? `${baseName} (${assignedUser.email})` : baseName;
                  })();

                  // Check if this matches the currently saved value
                  const savedAssignedTo = event?.event_metadata?.assigned_to;
                  const isAlreadySaved = String(formData.assigned_to) === String(savedAssignedTo);

                  if (isAlreadySaved) {
                    return (
                      <small className="text-success d-block mt-1">
                        ✓ Currently assigned to {displayName}
                      </small>
                    );
                  } else {
                    null
                  }
                })()}
              </div>
              </div>

              <hr className="my-3" />

              {/* <div className="mb-3">
                <label className="form-label">Any Other Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes..."
                />
              </div> */}

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="follow_up_required"
                    id="follow_up_required"
                    checked={formData.follow_up_required}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="follow_up_required">
                    Requires Follow-up
                  </label>
                </div>
              </div>

              {formData.follow_up_required && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Follow-up Date</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="follow_up_date"
                      value={formData.follow_up_date}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Follow-up Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      name="follow_up_notes"
                      value={formData.follow_up_notes}
                      onChange={handleChange}
                      placeholder="Follow-up instructions..."
                    />
                  </div>
                </>
              )}
            
            {/* Communication Composer Component */}
            {showCommunicationComposer && (
              <CommunicationComposer
                event={event}
                onCommunicationQueued={handleCommunicationQueued}
                onCancel={() => setShowCommunicationComposer(true)}
                isAnkrAdmin={isAnkrAdmin}
                deferSend={true}
              />
            )}

            {/* Show queued communications count */}
            {queuedCommunications.length > 0 && (
              <div className="alert alert-info mb-3">
                <strong>{queuedCommunications.length}</strong> communication(s) queued. They will be sent when you submit this activity.
              </div>
            )}

            <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving || !hasFormChanged()}>
              {saving ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onHide} disabled={saving}>
              Cancel
            </button>
            </div>
            </div>

          </form>
      </div>
    </>
  );
};

export default AddActivityModal;
